import { useQuery } from "@apollo/client";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Alert, Box, Button, Chip, CircularProgress, Link, Tab, Tabs, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useEffect, useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink, useParams } from "react-router-dom";

import { AccountBilling } from "../components/crm/AccountBilling";
import {
  AccountAmbulantCheckbox,
  AccountManagersCard,
  useAccountManagers,
} from "../components/crm/AccountManagersCard";
import { ContractItemAsset } from "../components/crm/ContractItemAsset";
import { ContractItemInventoryHistory } from "../components/crm/ContractItemInventoryHistory";
import { ContractItemOrders } from "../components/crm/ContractItemOrders";
import { CrmCard } from "../components/crm/CrmCard";
import { CrmDataTable } from "../components/crm/CrmDataTable";
import { CrmObjectIcon } from "../components/crm/CrmObjectIcon";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { CrmPath } from "../components/crm/CrmPath";
import { CrmRecordProvider } from "../components/crm/CrmRecordContext";
import { type CrmFieldItem, CrmFieldList, CrmSection } from "../components/crm/CrmSection";
import { CrmTimeline } from "../components/crm/CrmTimeline";
import { CrmValueView } from "../components/crm/CrmValueView";
import { LogisticsOrderCommandStatus } from "../components/logistics/LogisticsOrderCommandStatus";
import { LogisticsShippingLabelCard } from "../components/logistics/LogisticsShippingLabelCard";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName } from "../constants/resources";
import {
  CrmObject,
  CustomerServiceCrmRecordDocument,
  type CustomerServiceCrmRecordQuery,
  type CustomerServiceCrmRecordQueryVariables,
} from "../generated/graphql";
import { useCrmLabels } from "../hooks/useCrmLabels";
import { useSetBreadcrumbTitle } from "../layout/BreadcrumbTitleContext";
import {
  CRM_COLORS,
  crmObjectFromSlug,
  crmRecordPath,
  crmRelatedListPath,
  formatCrmCount,
  formatCrmDateTime,
  isMarketingObject,
  rememberCrmRecord,
} from "../lib/crm";
import { customerServiceOrganizationPath } from "../lib/customerServiceOrganizationRoutes";

type CrmRecordData = NonNullable<CustomerServiceCrmRecordQuery["customerServiceCrmRecord"]>;
type CrmSectionData = CrmRecordData["sections"][number];
type CrmRelatedData = CrmRecordData["related"][number];

type TabKey =
  | "feed"
  | "details"
  | "portal"
  | "license"
  | "asset"
  | "orders"
  | "inventoryHistory"
  | "billing"
  | "related";

const CONTRACT_ITEM_RIGHT_COLUMN = new Set(["price", "contactPersonEmail"]);

type FieldGridProps = {
  object: CrmObject;
  values: CrmSectionData["values"];
  // Fields the backend keeps rather than Salesforce, after the section's own.
  extra?: CrmFieldItem[];
};

const LICENSE_SECTION = "license";

const FieldGrid = ({ object, values, extra = [] }: FieldGridProps) => {
  const labels = useCrmLabels();
  return (
    <CrmFieldList
      items={values
        .map<CrmFieldItem>(value => ({
          key: value.key,
          testId: `crm-field-${value.key}`,
          label: labels.field(object, value.key),
          gridColumn:
            object !== CrmObject.ContractItem
              ? undefined
              : value.key === "cancellationStage"
                ? "1 / -1"
                : CONTRACT_ITEM_RIGHT_COLUMN.has(value.key)
                  ? { xs: "auto", md: "2" }
                  : "auto",
          value:
            object === CrmObject.ContractItem && value.key === "licenseQrCode" ? (
              value.value ? (
                <QRCodeSVG value={value.value} size={100} role="img" aria-label={labels.field(object, value.key)} />
              ) : null
            ) : (
              <CrmValueView value={value} multiline />
            ),
        }))
        .concat(extra)}
    />
  );
};

const Section = ({
  object,
  section,
  extra,
}: {
  object: CrmObject;
  section: CrmSectionData;
  extra?: CrmFieldItem[];
}) => {
  const labels = useCrmLabels();
  return (
    <CrmSection id={`crm-section-${section.key}`} title={labels.section(section.key)}>
      <FieldGrid object={object} values={section.values} extra={extra} />
    </CrmSection>
  );
};

const RelatedListCard = ({ record, related }: { record: CrmRecordData; related: CrmRelatedData }) => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  return (
    <CrmCard
      flush
      testId={`crm-related-${related.key}`}
      icon={<CrmObjectIcon object={related.object} size="small" />}
      title={`${labels.related(related.key)} (${formatCrmCount(related.totalCount)})`}
      footer={
        related.totalCount > related.rows.length ? (
          <Link
            component={RouterLink}
            to={crmRelatedListPath(record.object, record.salesforceId, related.key, related.object)}
            sx={{ color: CRM_COLORS.link }}
          >
            {translate("crm.related.viewAll")}
          </Link>
        ) : undefined
      }
    >
      {related.rows.length === 0 ? (
        <Typography sx={{ px: "16px", pb: "12px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
          {translate("crm.related.empty")}
        </Typography>
      ) : (
        <CrmDataTable object={related.object} columns={related.columns} rows={related.rows} dense />
      )}
    </CrmCard>
  );
};

const Highlights = ({ record }: { record: CrmRecordData }) => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  return (
    <Box
      component="dl"
      aria-label={translate("crm.record.highlights")}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px 32px",
        m: 0,
        px: "16px",
        pb: "16px",
      }}
    >
      {record.highlights.map(value => (
        <Box key={value.key} sx={{ minWidth: 0, maxWidth: 260 }}>
          <Typography component="dt" sx={{ fontSize: "12px", color: CRM_COLORS.label }}>
            {labels.field(record.object, value.key)}
          </Typography>
          <Box component="dd" sx={{ m: 0, fontSize: "14px", minHeight: "20px", overflowWrap: "anywhere" }}>
            <CrmValueView value={value} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const RecordContent = ({ record, onSynced }: { record: CrmRecordData; onSynced: () => unknown }) => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  const { canAccess: canInviteManagers } = useCanAccess({ action: "list", resource: ResourceName.Invitation });
  const { canAccess: canManageBilling } = useCanAccess({ action: "list", resource: ResourceName.OrganizationBilling });
  const { canAccess: canManageLearning } = useCanAccess({ action: "list", resource: ResourceName.LearningGrant });
  // An account Salesforce deleted takes no new portal setup or invitations.
  const showAccountManagers =
    record.object === CrmObject.Account &&
    (canInviteManagers === true || canManageBilling === true || canManageLearning === true) &&
    !record.deletedInSalesforceAt;
  const accountManagers = useAccountManagers(record.salesforceId, !showAccountManagers);
  const showPortalTab =
    showAccountManagers &&
    (Boolean(accountManagers.error) || accountManagers.data?.customerServiceAccountManagers.eligible === true);
  const managers = accountManagers.data?.customerServiceAccountManagers;
  // Staff who manage the account's portal set whether it provides ambulant care.
  const ambulantField: CrmFieldItem[] =
    showAccountManagers && managers?.eligible
      ? [
          {
            key: "ambulant",
            testId: "crm-account-ambulant",
            label: translate("crm.accountAmbulant.label"),
            value: <AccountAmbulantCheckbox managers={managers} />,
          },
        ]
      : [];
  const isCase = record.object === CrmObject.Case;
  // A contract item's licence has its own tab rather than a Details section.
  const licenseSection =
    record.object === CrmObject.ContractItem
      ? record.sections.find(section => section.key === LICENSE_SECTION)
      : undefined;
  const backendLicenseMissing = licenseSection?.values.some(
    value => value.key === "backendLicenseExists" && value.value === "false"
  );
  const licenseValues = licenseSection?.values.filter(value => value.key !== "backendLicenseExists");
  const timelineInSidebar = record.hasTimeline && !isCase;
  const tabs: TabKey[] = isCase
    ? ["feed", "details", "related"]
    : record.object === CrmObject.ContractItem
      ? ["details", ...(licenseSection ? (["license"] as const) : []), "asset", "orders", "inventoryHistory", "related"]
      : [
          "details",
          ...(showPortalTab ? (["portal"] as const) : []),
          ...(record.object === CrmObject.Account ? (["billing"] as const) : []),
          "related",
        ];
  const [selectedTab, setTab] = useState<TabKey>(tabs[0]);
  // The portal tab appears once the account turns out to be B2B.
  const tab = tabs.includes(selectedTab) ? selectedTab : tabs[0];
  useSetBreadcrumbTitle(record.title);

  useEffect(() => {
    rememberCrmRecord({ object: record.object, salesforceId: record.salesforceId, title: record.title });
  }, [record.object, record.salesforceId, record.title]);

  const relatedCards = record.related.map(related => (
    <RelatedListCard key={related.key} record={record} related={related} />
  ));

  return (
    <Box>
      <Title title={record.title} />
      <CrmPageHeader
        object={record.object}
        overline={labels.object(record.object)}
        title={record.title}
        meta={record.syncedAt && translate("crm.syncedAt", { time: formatCrmDateTime(record.syncedAt) })}
        actions={
          <>
            {record.appOrganizations.map(organization => (
              <Chip
                key={organization.id}
                icon={<ApartmentOutlinedIcon />}
                component={RouterLink}
                to={customerServiceOrganizationPath(organization.id)}
                clickable
                label={
                  organization.name
                    ? translate("crm.record.appOrganization", { name: organization.name })
                    : translate("crm.record.appOrganizationUnnamed")
                }
                sx={{ backgroundColor: CRM_COLORS.cardBackground, border: `1px solid ${CRM_COLORS.cardBorder}` }}
              />
            ))}
            <Button
              href={record.salesforceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
            >
              {translate("crm.record.openInSalesforce")}
            </Button>
          </>
        }
      >
        <Highlights record={record} />
        {record.path && <CrmPath stages={record.path.stages} current={record.path.current} />}
      </CrmPageHeader>
      {record.object === CrmObject.Order && (
        <>
          <LogisticsOrderCommandStatus salesforceId={record.salesforceId} onSynced={onSynced} />
          <LogisticsShippingLabelCard
            key={record.salesforceId}
            salesforceId={record.salesforceId}
            onSynced={onSynced}
          />
        </>
      )}
      {record.deletedInSalesforceAt && (
        <Alert severity="warning" data-testid="crm-record-deleted-in-salesforce" sx={{ mb: "12px" }}>
          {translate("crm.record.deletedInSalesforce", { time: formatCrmDateTime(record.deletedInSalesforceAt) })}
          {record.mergedInto && (
            <Box component="span" sx={{ display: "block", mt: "4px" }}>
              {translate("crm.record.mergedInto")}{" "}
              <Link
                component={RouterLink}
                to={crmRecordPath(CrmObject.Account, record.mergedInto.salesforceId)}
                data-testid="crm-record-merged-into"
              >
                {record.mergedInto.title ?? record.mergedInto.salesforceId}
              </Link>
            </Box>
          )}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: timelineInSidebar || isCase ? "minmax(0, 3fr) minmax(0, 2fr)" : "1fr" },
          gap: "12px",
          alignItems: "start",
        }}
      >
        <CrmCard flush>
          <Tabs
            value={tab}
            onChange={(_event, value: TabKey) => setTab(value)}
            sx={{ px: "16px", borderBottom: `1px solid ${CRM_COLORS.cardBorder}`, minHeight: 40 }}
          >
            {tabs.map(key => (
              <Tab
                key={key}
                value={key}
                label={translate(`crm.record.tabs.${key}`)}
                sx={{ textTransform: "none", fontSize: "14px", minHeight: 40 }}
              />
            ))}
          </Tabs>
          <Box sx={{ p: "12px" }}>
            {tab === "feed" && <CrmTimeline object={record.object} salesforceId={record.salesforceId} bare />}
            {tab === "details" &&
              record.sections
                .filter(section => section !== licenseSection)
                .map(section => (
                  <Section
                    key={section.key}
                    object={record.object}
                    section={section}
                    extra={section.key === "information" ? ambulantField : undefined}
                  />
                ))}
            {tab === "license" &&
              licenseValues &&
              (backendLicenseMissing ? (
                <Alert severity="info">{translate("crm.record.noBackendLicense")}</Alert>
              ) : (
                <FieldGrid object={record.object} values={licenseValues} />
              ))}
            {tab === "portal" && <AccountManagersCard accountName={record.title} result={accountManagers} />}
            {tab === "billing" && (
              <AccountBilling
                key={record.salesforceId}
                salesforceId={record.salesforceId}
                deleted={Boolean(record.deletedInSalesforceAt)}
              />
            )}
            {tab === "inventoryHistory" && (
              <ContractItemInventoryHistory history={record.contractItemInventoryHistory ?? []} />
            )}
            {tab === "orders" && <ContractItemOrders orders={record.contractItemOrders ?? []} />}
            {tab === "asset" && <ContractItemAsset asset={record.contractItemAsset} />}
            {tab === "related" && <Box sx={{ display: "grid", gap: "12px" }}>{relatedCards}</Box>}
          </Box>
        </CrmCard>
        {timelineInSidebar && <CrmTimeline object={record.object} salesforceId={record.salesforceId} />}
        {isCase && <Box sx={{ display: "grid", gap: "12px" }}>{relatedCards}</Box>}
      </Box>
    </Box>
  );
};

const CrmRecord = ({ object, salesforceId }: { object: CrmObject; salesforceId: string }) => {
  const translate = useTranslate();
  const { data, loading, error, refetch } = useQuery<
    CustomerServiceCrmRecordQuery,
    CustomerServiceCrmRecordQueryVariables
  >(CustomerServiceCrmRecordDocument, { variables: { object, salesforceId }, fetchPolicy: "cache-and-network" });
  const onSynced = useCallback(() => {
    // Apollo exposes the refetch error through the existing record error view.
    void refetch().catch(() => undefined);
  }, [refetch]);
  const record = data?.customerServiceCrmRecord;
  if (error) return <Alert severity="error">{translate("crm.loadError")}</Alert>;
  if (loading && !record) return <CircularProgress aria-label={translate("crm.loading")} />;
  if (!record) return <Alert severity="info">{translate("crm.record.notFound")}</Alert>;
  return (
    <CrmRecordProvider
      key={`${record.object}-${record.salesforceId}`}
      object={record.object}
      salesforceId={record.salesforceId}
    >
      <RecordContent record={record} onSynced={onSynced} />
    </CrmRecordProvider>
  );
};

const CrmRecordRoute = () => {
  const translate = useTranslate();
  const { objectSlug, salesforceId } = useParams();
  const object = crmObjectFromSlug(objectSlug);
  if (!object || !salesforceId) return <Alert severity="warning">{translate("crm.record.notFound")}</Alert>;
  const record = <CrmRecord object={object} salesforceId={salesforceId} />;
  return isMarketingObject(object) ? (
    <RequirePermission permission={ResourceName.Marketing}>{record}</RequirePermission>
  ) : (
    record
  );
};

export const CrmRecordPage = () => (
  <RequirePermission permission={ResourceName.Crm}>
    <CrmRecordRoute />
  </RequirePermission>
);
